import { MutableRefObject, useCallback, useRef, useState } from "react";
import { useIsomorphicLayoutEffect } from "@/lib/hooks/use-isomorphic-layout-effect";

type Dimensions = {
  width: number;
  height: number;
  domRect: DOMRect | undefined;
};

/**
 * Hook to observe the size of a DOM element.
 * @param active - Whether the observer should be active. Defaults to true.
 * @returns A tuple containing the ref for the element and the size of the element.
 */
export function useResizeObserver<T extends HTMLElement>(
  active = true,
): [MutableRefObject<T>, Dimensions] {
  const ref = useRef<T>(null) as MutableRefObject<T>;
  const [size, setSize] = useState<Dimensions>({
    width: 0,
    height: 0,
    domRect: undefined,
  });

  // Use a ref for the observer itself, so it persists across renders
  // and we don't need to put it in state (which can cause render issues).
  const observerRef = useRef<ResizeObserver | null>(null);
  // requestAnimationFrame ref
  const rafRef = useRef<number | null>(null);

  const updateSize = useCallback((entries: ResizeObserverEntry[]) => {
    const entry = entries[0];
    if (entry) {
      // Cancel any pending frame to avoid stale updates
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const { width, height } = entry.contentRect;
        setSize({
          width,
          height,
          // getBoundingClientRect is safe here — we're in a new frame,
          // outside the ResizeObserver callback
          domRect: entry.target.getBoundingClientRect(),
        });
        rafRef.current = null;
      });
    }
  }, []);

  useIsomorphicLayoutEffect(() => {
    // Ensure we have an element to observe
    if (!ref.current) {
      return;
    }

    // Initialize observer if it doesn't exist
    if (!observerRef.current) {
      observerRef.current = new ResizeObserver(updateSize);
    }

    // Get a local reference to the observer for clarity and to ensure cleanup works
    const currentObserver = observerRef.current;
    const currentElement = ref.current;

    if (active) {
      currentObserver.observe(currentElement);
    } else {
      currentObserver.disconnect();
    }

    // Cleanup function: disconnect the observer when the component unmounts
    return () => {
      currentObserver.disconnect();
      // Cancel any pending rAF on cleanup to avoid setState after unmount
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [active, updateSize]);

  return [ref, size];
}
